import { TimeRangePickerProps } from "antd";
import dayjs from "dayjs";
import customParseFormat from "dayjs/plugin/customParseFormat";
import isBetween from "dayjs/plugin/isBetween";
import isSameOrAfter from "dayjs/plugin/isSameOrAfter";
import isSameOrBefore from "dayjs/plugin/isSameOrBefore";
import isToday from "dayjs/plugin/isToday";
import quarterOfYear from "dayjs/plugin/quarterOfYear";
import weekOfYear from "dayjs/plugin/weekOfYear";
dayjs.extend(customParseFormat);
dayjs.extend(quarterOfYear);
dayjs.extend(weekOfYear);
dayjs.extend(isToday);
dayjs.extend(isSameOrAfter);
dayjs.extend(isSameOrBefore);
dayjs.extend(isBetween);
dayjs.locale("ru");

export const today = dayjs();
export const yesterday = today.subtract(1, "day");
export const weekAgo = today.subtract(1, "week");
export const weekAgo2 = today.subtract(1, "week");
export const weekAgo3 = today.subtract(2, "week");

export const getComparisonInterval = (
  start: string,
  end: string,
): { compareStart: string; compareEnd: string } => {
  const intervalStart = dayjs(start, "YYYY-MM-DD");
  const intervalEnd = dayjs(end, "YYYY-MM-DD");
  const diffDays = intervalEnd.diff(intervalStart, "day") + 1;

  const today = dayjs().startOf("day");
  const yesterday = today.subtract(1, "day");
  const dayBeforeYesterday = today.subtract(2, "day");

  // Сегодня / Вчера / Позавчера
  if (diffDays === 1) {
    if (
      intervalStart.isSame(today, "day") ||
      intervalStart.isSame(yesterday, "day") ||
      intervalStart.isSame(dayBeforeYesterday, "day")
    ) {
      return {
        compareStart: dayjs(intervalStart)
          .subtract(7, "day")
          .format("YYYY-MM-DD"),
        compareEnd: dayjs(intervalEnd).subtract(7, "day").format("YYYY-MM-DD"),
      };
    }
  }

  // Неделя
  if (diffDays === 7) {
    return {
      compareStart: dayjs(intervalStart)
        .subtract(1, "week")
        .format("YYYY-MM-DD"),
      compareEnd: dayjs(intervalEnd).subtract(1, "week").format("YYYY-MM-DD"),
    };
  }

  // Месяц
  if (
    intervalStart.date() === 1 &&
    intervalEnd.isSame(intervalStart.endOf("month"), "day")
  ) {
    return {
      compareStart: dayjs(intervalStart)
        .subtract(1, "month")
        .startOf("month")
        .format("YYYY-MM-DD"),
      compareEnd: dayjs(intervalStart)
        .subtract(1, "month")
        .endOf("month")
        .format("YYYY-MM-DD"),
    };
  }

  // Квартал
  const startQuarter = intervalStart.startOf("quarter");
  const endQuarter = intervalStart.endOf("quarter");

  if (
    intervalStart.isSame(startQuarter, "day") &&
    intervalEnd.isSame(endQuarter, "day")
  ) {
    return {
      compareStart: dayjs(startQuarter)
        .subtract(1, "quarter")
        .format("YYYY-MM-DD"),
      compareEnd: dayjs(endQuarter).subtract(1, "quarter").format("YYYY-MM-DD"),
    };
  }

  // Интервал < 7 дней
  if (diffDays < 7) {
    return {
      compareStart: dayjs(intervalStart)
        .subtract(7, "day")
        .format("YYYY-MM-DD"),
      compareEnd: dayjs(intervalEnd).subtract(7, "day").format("YYYY-MM-DD"),
    };
  }

  // Интервал > 7 дней: вернуть такой же интервал после конца
  if (diffDays > 7) {
    return {
      compareStart: dayjs(intervalEnd).add(1, "day").format("YYYY-MM-DD"),
      compareEnd: dayjs(intervalEnd).add(diffDays, "day").format("YYYY-MM-DD"),
    };
  }

  return {
    compareStart: dayjs().format("YYYY-MM-DD"),
    compareEnd: dayjs().format("YYYY-MM-DD"),
  };
};

export const getDatePreset = (
  type: "full" | "medium",
): TimeRangePickerProps["presets"] => {
  if (type === "full") {
    return [
      {
        label: "Вчера",
        value: [dayjs().subtract(1, "days"), dayjs().subtract(1, "days")],
      },
      {
        label: "Позавчера",
        value: [dayjs().subtract(2, "days"), dayjs().subtract(2, "days")],
      },
      {
        label: "Эта нед",
        value: [dayjs().startOf("week"), dayjs().endOf("week")],
      },
      {
        label: "Пред нед",
        value: [
          dayjs().subtract(1, "week").startOf("week"),
          dayjs().startOf("week").add(-1, "d"),
        ],
      },
      {
        label: "Этот мес",
        value: [dayjs().startOf("month"), dayjs().endOf("month")],
      },
      {
        label: "Пред мес",
        value: [
          dayjs().subtract(1, "month").startOf("month"),
          dayjs().startOf("month").add(-1, "d"),
        ],
      },
      {
        label: "Этот кварт",
        value: [dayjs().startOf("quarter"), today.endOf("quarter")],
      },
      {
        label: "Пред кварт",
        value: [
          dayjs().subtract(1, "quarter").startOf("quarter"),
          dayjs().startOf("quarter").add(-1, "d"),
        ],
      },
    ];
  }
};
